import React, { useCallback, useMemo } from 'react';
import { SocialUserInfo } from '../profile-views/ProfileViews';
import { CollegeFollowCard } from '../follows/CollegeFollowCard';

import { gql } from 'urql';
import {
    GetFollowingPlayersForCollegesQuery,
    GetFollowingPlayersForCollegesQueryVariables,
    GetFollowingPlayersForCollegesDocument,
    Espin_Follow_Type_Enum,
    UnfollowMutation,
    UnfollowMutationVariables,
    UnfollowDocument,
} from '../../../generated/spin-graphql';

import { useAuthenticatedMutation, useAuthenticatedQuery } from 'client/hooks';
import { useAppState } from 'client/providers/AppProvider';
import { FollowingIcon } from '../../svg/dashboard/FollowingIcon';

type Props = {
    allFollowers?: SocialUserInfo[];
};

gql`
    query GetFollowingPlayersForColleges($collegeId: uuid!) {
        espin_follow(
            where: {
                follow_type: { _eq: college_follows_player }
                college_id: { _eq: $collegeId }
                player_user: { deleted_at: { _is_null: true } }
            }
        ) {
            ...CollegeFollowCard
        }
        high_school_following: espin_follow(
            where: { college_id: { _eq: $collegeId }, follow_type: { _eq: college_follows_high_school } }
        ) {
            ...CollegeFollowCard
        }
        espin_graduating_class {
            grad_class_years
        }
    }
`;
export const CollegeFollowing: React.FC<Props> = () => {
    const { user } = useAppState();

    const { isReady, data } = useAuthenticatedQuery<
        GetFollowingPlayersForCollegesQuery,
        GetFollowingPlayersForCollegesQueryVariables
    >(
        {
            query: GetFollowingPlayersForCollegesDocument,
            variables: {
                collegeId: user?.college_profile?.college.id,
            },
            pause: !user,
        },
        ['espin_follow'],
    );

    const years = useMemo(() => data?.espin_graduating_class?.[0].grad_class_years.split(',').map((s) => s.trim()), [
        data?.espin_graduating_class,
    ]);

    const [__, unFollow] = useAuthenticatedMutation<UnfollowMutation, UnfollowMutationVariables>(UnfollowDocument);

    const handleUnfollowClicked = useCallback(
        (userId: string, followType: Espin_Follow_Type_Enum) => {
            unFollow(
                {
                    where: {
                        college_id: { _eq: user?.college_profile?.college.id },
                        follow_type: { _eq: followType },
                        ...(followType !== Espin_Follow_Type_Enum.CollegeFollowsHighSchool && {
                            player_user_id: { _eq: userId },
                        }),
                        ...(followType === Espin_Follow_Type_Enum.CollegeFollowsHighSchool && {
                            high_school_id: { _eq: userId },
                        }),
                    },
                },
                { additionalTypenames: ['espin_follow'] },
            );
        },
        [unFollow, user?.college_profile?.college.id],
    );
    const listOfFollows = useMemo(
        () =>
            data?.espin_follow.concat(data?.high_school_following).map((f) => f?.player_user?.id ?? f?.high_school.id),
        [data?.espin_follow, data?.high_school_following],
    );
    return (
        <>
            {isReady && (
                <div className="follow-container">
                    <CollegeFollowCard
                        hsFollowers={data?.high_school_following}
                        listOfFollows={listOfFollows}
                        followers={data.espin_follow}
                        next4Years={years}
                        fakeCardLabel={'Player'}
                        fakeCardSubLabel={'Upgrade to Premium to follow players!'}
                        noContentIcon={<FollowingIcon />}
                        noContentLabel={'You are not following anyone yet'}
                        handleFollowButtonClicked={(
                            userId: string,
                            following: boolean,
                            followType: Espin_Follow_Type_Enum,
                        ) => handleUnfollowClicked(userId, followType)}
                    />
                </div>
            )}
        </>
    );
};
